import { WistiaPlayer } from '@wistia/wistia-player-react'
import { useEffect, useRef, useState } from 'react'

import heroStyles from '~/components-v2/hero/hero.css?url'
import styles from '~/styles/pages/home.css?url'
import tabAccordionStyles from '~/components-v2/tab-accordion/tab-accordion.css?url'
import videoSliderStyles from '~/components-v2/video-slider/video-slider.css?url'

import EngineeringDemoStyles from '~/components-v2/engineering-demo/engineering-demo.css?url'
import integrationsCarouselStyles from '~/components/integrations-carousel/integrations-carousel.css?url'
import cardStyles from '~/components-v2/card/card.css?url'
import typewriterInputStyles from '~/components-v2/typewriter-input/typewriterInput.css?url'
import gridStyles from '~/styles/components/grid.css?url'

import { fragmentLinks } from '~/fragments'
import { CallToAction } from '~/utils/a-b'
import {
  Button,
  Stack,
  VideoSlider,
  LinkArrow,
  EngineeringDemo,
  TypewriterInput,
  TabAccordion,
} from '~/components-v2'
import type { TabAccordionItem } from '~/components-v2'
import { useLoaderData } from 'react-router'
import { IntegrationsCarousel } from '~/components/integrations-carousel/integrations-carousel'
import {
  CustomersMarquee,
  customersMarqueeLinks,
} from '~/components/shared/customers-marquee'
import { getBlogPosts } from '~/services/contentful/blog.server'
import { fetchDocsDemos } from '~/services/docs-demos'
import {
  FeatureCardGrid,
  featureCardGridLinks,
} from '~/components-v2/feature-card-grid/feature-card-grid'
import { ScrollingBullets } from './fragments/scrolling-bullets'
import { CarouselHero } from './fragments/carousel-hero'
import { SelfServices } from './fragments/self-services'
import { Connectors } from './fragments/connectors'
import { AnalyticsJourney } from './fragments/analytics-journey'

import {
  PostCollection,
  postCollectionLinks,
} from '~/components-v2/post-collection/post-collection'
import { videoSliderData } from './data/video-slider'
import { toolLinkCardsData } from './data/tool-link-cards'
import { toolCardsData } from './data/tool-cards'

const cliCodePreview = (
  <pre className="tab-accordion-code-block">
    <code>
      <span>{'{\n  '}</span>
      <span className="tab-accordion-code-key">"model"</span>
      <span>{': {\n    '}</span>
      <span className="tab-accordion-code-key">"id"</span>
      <span>{': "529258b9-7dbd-4bb6-8869-028a437dd384",\n    '}</span>
      <span className="tab-accordion-code-key">"createdAt"</span>
      <span>{': "2026-04-15T23:24:39.501Z",\n    '}</span>
      <span className="tab-accordion-code-key">"updatedAt"</span>
      <span>{': "2026-04-15T23:24:39.501Z",\n    '}</span>
      <span className="tab-accordion-code-key">"deletedAt"</span>
      <span>{': null,\n    '}</span>
      <span className="tab-accordion-code-key">"connectionId"</span>
      <span>{': "0b2493a4-b88e-4561-8285-777453c7b956",\n    '}</span>
      <span className="tab-accordion-code-key">"name"</span>
      <span>{': "optimize-for-ai",\n    '}</span>
      <span className="tab-accordion-code-key">"organizationId"</span>
      <span>{': "3cf4fd8a-29e8-408b-ac63-f9a687d0186f",\n    '}</span>
      <span className="tab-accordion-code-key">"userId"</span>
      <span>{': "57da3a10-45d8-449e-9a04-5d1892c0d403",\n    '}</span>
      <span className="tab-accordion-code-key">"environmentConnectionId"</span>
      <span>{': null,\n    '}</span>
      <span className="tab-accordion-code-key">"baseModelId"</span>
      <span>{': "8026c838-5331-45f3-a97a-cf8569da213f",\n    '}</span>
      <span className="tab-accordion-code-key">"version"</span>
      <span>{': "0",\n    '}</span>
      <span className="tab-accordion-code-key">"modelKind"</span>
      <span>{': "BRANCH",\n    '}</span>
      <span className="tab-accordion-code-key">"hasGit"</span>
      <span>{': false,\n    '}</span>
      <span className="tab-accordion-code-key">"pullRequestUrl"</span>
      <span>{': null,\n    '}</span>
      <span className="tab-accordion-code-key">"gitSha"</span>
      <span>{': null,\n    '}</span>
      <span className="tab-accordion-code-key">"lastPrCommitAt"</span>
      <span>{': null,\n    '}</span>
      <span className="tab-accordion-code-key">"pullRequestRequired"</span>
      <span>{': false,\n    '}</span>
      <span className="tab-accordion-code-key">"branchPerPullRequest"</span>
      <span>{': false,\n    '}</span>
      <span className="tab-accordion-code-key">"createdByPrincipal"</span>
      <span>
        {
          ': "{\\"userId\\":\\"57da3a10-45d8-449e-9a04-5d1892c0d403\\",\\"additionalContext\\":\\"API key id: aa30349a-5011-4cd7-9e58-58b3e78c1\\"}",\n    '
        }
      </span>
      <span className="tab-accordion-code-key">"disableAutoSync"</span>
      <span>{': null\n  }\n}'}</span>
    </code>
  </pre>
)

const modelingAgentItems: TabAccordionItem[] = [
  {
    id: 'modeling',
    label: 'Built-in Modeling Agent',
    description: (
      <>
        Omni's built-in agent helps you build metrics, add AI context,
        incorporate existing documentation, and more.{' '}
        <LinkArrow href="https://omni.co/blog/announcing-omnis-modeling-agent">
          Learn more
        </LinkArrow>
      </>
    ),
    screenshot: '/images/modeling-agents/build-in-modeling-agent.webp',
    screenshotAlt: 'Omni Modeling Agent interface',
    screenshotAspectRatio: '5 / 3',
  },
  {
    id: 'skills',
    label: 'Skills',
    description: (
      <>
        Invoke pre-packaged agent behaviors directly in your chat interface to
        build models, run queries, and manage content.{' '}
        <LinkArrow href="https://omni.co/blog/introducing-omnis-agent-skills">
          Learn more
        </LinkArrow>
      </>
    ),
    screenshot: '/images/modeling-agents/skills.webp',
    screenshotAlt: 'Omni Skills interface',
    screenshotAspectRatio: '5 / 3',
  },
  {
    id: 'cli',
    label: 'Command Line Interface (CLI)',
    description: (
      <>
        Leverage Omni's suite of APIs to manage your Omni instance from the
        terminal.{' '}
        <LinkArrow href="https://docs.omni.co/developers/cli" external={true}>
          Learn more
        </LinkArrow>
      </>
    ),
    screenshotContent: cliCodePreview,
    screenshotLabel: 'Omni CLI JSON response',
    screenshotAspectRatio: '5 / 3',
  },
]

const title = 'The AI analytics platform'
const description =
  'Omni Analytics turns your data into a source of truth for AI, so anyone can get answers they trust.'

const LazyHomepageWistiaPlayer = ({ mediaId }: { mediaId: string }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [shouldLoad, setShouldLoad] = useState(false)

  useEffect(() => {
    const target = containerRef.current
    if (!target) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setShouldLoad(true)
          observer.disconnect()
        }
      },
      { rootMargin: '600px' },
    )

    observer.observe(target)
    return () => observer.disconnect()
  }, [])

  return (
    <div className="video-frame__wistia" ref={containerRef}>
      {shouldLoad && <WistiaPlayer transparentLetterbox mediaId={mediaId} />}
    </div>
  )
}

export const links = () => [
  { rel: 'stylesheet', href: styles },
  { rel: 'stylesheet', href: heroStyles },
  { rel: 'stylesheet', href: tabAccordionStyles },
  { rel: 'stylesheet', href: videoSliderStyles },
  { rel: 'stylesheet', href: EngineeringDemoStyles },
  { rel: 'stylesheet', href: cardStyles },
  { rel: 'stylesheet', href: gridStyles },
  { rel: 'stylesheet', href: integrationsCarouselStyles },
  { rel: 'stylesheet', href: typewriterInputStyles },
  ...postCollectionLinks(),
  ...featureCardGridLinks(),
  ...customersMarqueeLinks(),
  ...fragmentLinks.footerDemoSection(),
]
export { meta } from '~/utils'

export const loader = async () => {
  const { posts } = await getBlogPosts({ limit: 6 })
  const docsDemos = await fetchDocsDemos().catch(() => undefined)
  return {
    docsDemos,
    description,
    posts,
    title,
  }
}

export default function Home() {
  const { docsDemos, posts, title } = useLoaderData<typeof loader>()

  return (
    <>
      <header className="hero product-shot-inline home">
        <Stack as="hgroup" gap={4}>
          <h1 className="heading-xxxl text-brand-dark">{title}</h1>

          <h2 className="body-l text-primary">
            Omni turns your data into a source of truth for AI, so anyone can
            get answers they trust.
          </h2>

          <CallToAction />
        </Stack>

        <div className="product">
          <div className="product-imagery">
            <TypewriterInput />
          </div>
        </div>
      </header>

      <CustomersMarquee classNames="home-insights-marquee" mobileGrid={false} />

      <ScrollingBullets />

      <section className="page-section section-spacing video-slider-section">
        <div className="page-section__inner video-slider-section__inner">
          <hgroup className="video-slider-header">
            <div className="text-combo">
              <h2 className="heading-xl text-brand-dark">
                Make decisions faster with AI
              </h2>
              <p className="body-l">
                Put your analysis on autopilot. Instantly diagnose metric
                changes and schedule AI-written summaries to go out where your
                team works.
              </p>
            </div>

            <Button to="/ai">Learn more</Button>
          </hgroup>
          <VideoSlider videoSliderData={videoSliderData} />
        </div>
      </section>

      <section className="page-section page-section--lg section-spacing feature-card-section feature-card-section--compact">
        <div className="page-section__inner feature-card-section__inner">
          <hgroup className="feature-card-section__header">
            <h2 className="heading-xl text-brand-dark">
              Use familiar analytics tools, all in one place
            </h2>
          </hgroup>

          <FeatureCardGrid
            cards={toolCardsData}
            columns={2}
            showLinkIcon={true}
          />
        </div>
      </section>

      <CarouselHero />

      <AnalyticsJourney />

      <section className="page-section page-section--lg video-band bg-brand-dark">
        <div className="page-section__inner">
          <hgroup className="video-band-copy">
            <h2 className="heading-xl text-brand">
              Capture your team's knowledge in a semantic model
            </h2>
            <p className="body-l text-inverse">
              Experts define core logic, while everyone else contributes domain
              expertise. AI gets better with every use.
            </p>
            <Button
              href="https://community.omni.co/t/improving-ai-answer-quality-a-practical-guide/356"
              external={true}
            >
              Learn more
            </Button>
          </hgroup>

          <div className="video-frame">
            <LazyHomepageWistiaPlayer mediaId="hxqs7tdcdj" />
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="816"
              height="467"
              viewBox="0 0 816 467"
              fill="none"
            />
          </div>
        </div>
      </section>

      <Connectors />

      <TabAccordion
        name="modeling-agents"
        heading="Model data and generate AI context with agents"
        items={modelingAgentItems}
        className="section-spacing"
      />

      <section className="page-section page-section--lg section-spacing feature-card-section feature-card-section--spacious">
        <div className="page-section__inner feature-card-section__inner">
          <hgroup className="feature-card-section__header">
            <h2 className="heading-xl text-brand-dark">
              Safely launch and scale AI in your organization
            </h2>
          </hgroup>
          <FeatureCardGrid
            cards={toolLinkCardsData}
            columns={3}
            showLinkIcon={true}
          />
        </div>
      </section>

      <section className="page-section page-section--lg section-spacing integrations">
        <div className="page-section__inner">
          <IntegrationsCarousel
            header={
              <hgroup>
                <div className="text-combo">
                  <h2 className="heading-xl text-brand-dark">
                    Start analyzing with AI&nbsp;immediately
                  </h2>
                  <p className="body-l text-primary">
                    Get started in minutes by connecting your data.
                  </p>
                </div>
              </hgroup>
            }
          />
        </div>
      </section>

      <div
        className="page-section page-section--lg video-band video-band--scroll-target bg-brand"
        id="short-demo"
      >
        <div className="page-section__inner">
          <hgroup className="video-band-copy">
            <h2 className="heading-xxl">See Omni in Action</h2>
            <p className="body-l text-primary">
              Watch how easy it is to chat with your data and get instant,
              accurate answers.
            </p>
            <Button to="/request-demo" variant="inverse">
              Schedule a 1:1 Demo
            </Button>
          </hgroup>

          <div className="video-frame">
            <LazyHomepageWistiaPlayer mediaId="10qmv2fjfu" />
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="816"
              height="467"
              viewBox="0 0 816 467"
              fill="none"
            />
          </div>
        </div>
      </div>

      <section className="page-section page-section--lg section-spacing unlock-self-service">
        <div className="page-section__inner">
          <hgroup>
            <h2 className="heading-xl text-brand-dark">
              Hear from our customers
            </h2>
          </hgroup>
          <SelfServices />
        </div>
      </section>

      <EngineeringDemo docsDemos={docsDemos} />

      <section className="page-section page-section--lg section-spacing post-collection-section read-more">
        <div className="page-section__inner">
          <PostCollection
            header={
              <hgroup>
                <h2 className="heading-xl text-brand-dark">
                  Read more about our approach
                </h2>
              </hgroup>
            }
            posts={posts}
            variant="carousel"
          />
        </div>
      </section>
    </>
  )
}
